const withRef = (base: string, ref: string): string =>
  `${base}/${encodeURIComponent(ref)}`;

export const APP_PATHS = {
  dashboard: "/dashboard",
  /** Previous analytics-chat dashboard, kept while the merchant metrics dashboard rolls out. */
  dashboardV0: "/dashboard-v0",
  /** Full-page setup checklist — temporary entry point while onboarding is rolled out. */
  onboarding: "/onboarding",
  /** Every import of the company (catalog, contacts, vouchers, messages, FAQ…), with what was not imported. */
  importHistory: "/imports",
  messaging: {
    inbox: "/messaging/inbox",
    contacts: "/messaging/contacts",
    contactNew: "/messaging/contacts/new",
    contactImport: "/messaging/contacts/import",
    contactLists: "/messaging/contacts/lists",
    contactSegments: "/messaging/contacts/segments",
    contact: (ref: string) => withRef("/messaging/contacts", ref),
    contactEdit: (ref: string) => `${withRef("/messaging/contacts", ref)}/edit`,
    lists: "/messaging/lists",
    list: (ref: string) => withRef("/messaging/lists", ref),
    segments: "/messaging/segments",
    segment: (ref: string) => withRef("/messaging/segments", ref),
    tickets: "/messaging/tickets",
    ticketNew: "/messaging/tickets/new",
    ticket: (ref: string) => withRef("/messaging/tickets", ref),
    ticketEdit: (ref: string) => `${withRef("/messaging/tickets", ref)}/edit`,
    quickResponses: "/messaging/quick-responses",
    quickResponseNew: "/messaging/quick-responses/new",
    quickResponse: (ref: string) => withRef("/messaging/quick-responses", ref),
    quickResponseEdit: (ref: string) =>
      `${withRef("/messaging/quick-responses", ref)}/edit`,
    tags: "/messaging/tags",
    tagNew: "/messaging/tags/new",
    tagEdit: (ref: string) => `${withRef("/messaging/tags", ref)}/edit`,
    attributes: "/messaging/attributes",
    attributeNew: "/messaging/attributes/new",
    attributeEdit: (ref: string) =>
      `${withRef("/messaging/attributes", ref)}/edit`,
    channels: "/messaging/channels",
    channelNew: "/messaging/channels/new",
    channelEdit: (ref: string) => `${withRef("/messaging/channels", ref)}/edit`,
  },
  catalog: {
    orders: "/catalog/orders",
    orderNew: "/catalog/orders/new",
    orderFrom: (ref: string) => withRef("/catalog/orders/new", ref),
    order: (ref: string) => withRef("/catalog/orders", ref),
    orderEdit: (ref: string) => `${withRef("/catalog/orders", ref)}/edit`,
    reservations: "/catalog/reservations",
    reservationNew: "/catalog/reservations/new",
    reservation: (ref: string) => withRef("/catalog/reservations", ref),
    reservationEdit: (ref: string) =>
      `${withRef("/catalog/reservations", ref)}/edit`,
    products: "/catalog/products",
    productNew: "/catalog/products/new",
    productImport: "/catalog/products/import",
    productArchived: "/catalog/products/archived",
    product: (ref: string) => withRef("/catalog/products", ref),
    productEdit: (ref: string) => `${withRef("/catalog/products", ref)}/edit`,
    categories: "/catalog/categories",
    whatsappStorefront: "/catalog/whatsapp-storefront",
    diningTables: "/catalog/tables",
    diningTablesPrint: "/catalog/tables/print",
    discountCodes: "/catalog/discount-codes",
    categoryNew: "/catalog/categories/new",
    categoryArchived: "/catalog/categories/archived",
    categoryEdit: (ref: string) =>
      `${withRef("/catalog/categories", ref)}/edit`,
    vouchers: "/catalog/vouchers",
    voucherImport: "/catalog/vouchers/import",
    voucherBarcodes: "/catalog/vouchers/barcodes",
    voucherBroadcast: "/catalog/vouchers/broadcast",
    delivery: "/catalog/delivery",
    merchants: "/catalog/delivery/merchants",
    merchantNew: "/catalog/delivery/merchants/new",
    merchant: (ref: string) => withRef("/catalog/delivery/merchants", ref),
    merchantEdit: (ref: string) =>
      `${withRef("/catalog/delivery/merchants", ref)}/edit`,
    couriers: "/catalog/delivery/couriers",
    courierNew: "/catalog/delivery/couriers/new",
    courier: (ref: string) => withRef("/catalog/delivery/couriers", ref),
    courierEdit: (ref: string) =>
      `${withRef("/catalog/delivery/couriers", ref)}/edit`,
    parcels: "/catalog/delivery/parcels",
    parcelNew: "/catalog/delivery/parcels/new",
    parcelFrom: (ref: string) => withRef("/catalog/delivery/parcels/new", ref),
    parcel: (ref: string) => withRef("/catalog/delivery/parcels", ref),
    parcelEdit: (ref: string) =>
      `${withRef("/catalog/delivery/parcels", ref)}/edit`,
  },
  workflow: {
    dashyKpiRecap: "/workflow/bots/dashy-kpi-recap/workflow",
    templates: "/workflow/templates",
    template: (ref: string) => withRef("/workflow/templates", ref),
    templateRequests: "/workflow/templates/requests",
    templateRequestNew: "/workflow/templates/requests/new",
    templateRequest: (ref: string) =>
      withRef("/workflow/templates/requests", ref),
    templateRequestEdit: (ref: string) =>
      `${withRef("/workflow/templates/requests", ref)}/edit`,
    forms: "/workflow/forms",
    formNew: "/workflow/forms/new",
    form: (ref: string) => withRef("/workflow/forms", ref),
    formEdit: (ref: string) => `${withRef("/workflow/forms", ref)}/edit`,
    formResponses: (ref: string) =>
      `${withRef("/workflow/forms", ref)}/responses`,
    bots: "/workflow/bots",
    botWorkflow: (botId: string) =>
      `${withRef("/workflow/bots", botId)}/workflow`,
    automations: "/workflow/automations/channel-messages",
    automationIntegrations: "/workflow/automations/integrations",
    autoResponses: "/workflow/bots",
    automationFlows: "/workflow/automations/flows",
    automationFlowNew: "/workflow/automations/flows/new",
    channelMessages: "/workflow/automations/channel-messages",
    pendingMessages: "/workflow/automations/pending-messages",
    pendingMessagesImport: "/workflow/automations/pending-messages/import",
    pendingMessage: (ref: string) =>
      withRef("/workflow/automations/pending-messages", ref),
    aiAgents: "/workflow/ai-agents",
    aiAssistance: "/workflow/ai-assistance",
    templateCategories: "/workflow/templates/categories",
    templateCategoryNew: "/workflow/templates/categories/new",
    templateCategory: (ref: string) =>
      withRef("/workflow/templates/categories", ref),
    templateCategoryEdit: (ref: string) =>
      `${withRef("/workflow/templates/categories", ref)}/edit`,
    templateFolders: "/workflow/templates/folders",
    templateFolderNew: "/workflow/templates/folders/new",
    templateFolder: (ref: string) =>
      withRef("/workflow/templates/folders", ref),
    templateFolderEdit: (ref: string) =>
      `${withRef("/workflow/templates/folders", ref)}/edit`,
    templateVariables: "/workflow/templates/variables",
    templateVariableNew: "/workflow/templates/variables/new",
    templateVariableEdit: (ref: string) =>
      `${withRef("/workflow/templates/variables", ref)}/edit`,
  },
  campaigns: {
    new: "/campaigns/new",
    history: "/campaigns/history",
    detail: (ref: string) => withRef("/campaigns/history", ref),
    edit: (ref: string) => `${withRef("/campaigns", ref)}/edit`,
  },
  payment: {
    subscription: "/payment/subscription",
    links: "/payment/links",
    authorizations: "/payment/authorizations",
    savedCards: "/payment/saved-cards",
    simulator: "/payment/simulator",
  },
  settings: {
    company: "/settings/company",
    agents: "/settings/team/agents",
    agentNew: "/settings/team/agents/new",
    agentArchived: "/settings/team/agents/archived",
    agent: (ref: string) => withRef("/settings/team/agents", ref),
    agentEdit: (ref: string) => `${withRef("/settings/team/agents", ref)}/edit`,
    agentGroups: "/settings/team/agent-groups",
    agentGroupNew: "/settings/team/agent-groups/new",
    agentGroupArchived: "/settings/team/agent-groups/archived",
    agentGroupEdit: (ref: string) =>
      `${withRef("/settings/team/agent-groups", ref)}/edit`,
    catalogIntegration: "/settings/catalog-integration",
    catalogProducts: "/settings/catalog-integration/products",
    audit: "/settings/audit",
    notifications: "/settings/notifications",
    faq: "/settings/faq",
    faqImport: "/settings/faq/import",
    faqNew: "/settings/faq/new",
    faqItem: (ref: string) => withRef("/settings/faq", ref),
    faqEdit: (ref: string) => `${withRef("/settings/faq", ref)}/edit`,
    currencies: "/settings/currencies",
    currencyNew: "/settings/currencies/new",
    currency: (ref: string) => withRef("/settings/currencies", ref),
    currencyEdit: (ref: string) =>
      `${withRef("/settings/currencies", ref)}/edit`,
    plan: "/settings/plan",
  },
  platform: {
    subscriptionPlans: "/platform/subscription-plans",
    companies: "/platform/companies",
    companyNew: "/platform/companies/new",
    company: (ref: string) => withRef("/platform/companies", ref),
    companyEdit: (ref: string) => `${withRef("/platform/companies", ref)}/edit`,
    modules: "/platform/modules",
    courierRates: "/platform/courier-rates",
    moduleEdit: (ref: string) => `${withRef("/platform/modules", ref)}/edit`,
    dashboard: "/platform/dashboard",
    usage: "/platform/usage",
    usageExport: "/platform/usage/export",
    billing: "/platform/billing",
    billingNew: "/platform/billing/new",
    tutorials: "/platform/tutorials",
    status: "/platform/status",
  },
  notFound: "/not-found",
} as const;
